import React, { useState, useRef } from 'react';
import { Box, Typography, CircularProgress, List, ListItem, IconButton, Button, Select, MenuItem, FormControl } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowBack } from '@mui/icons-material';
import { fileToBase64 } from '../../utils/fileToBase64';
import { apiPost } from '../../services/apiClient';
import { buildApiUrl } from '../../config/apiEndpoints';

const Drag = () => {
  const { t } = useTranslation();

  const [droppedFiles, setDroppedFiles] = useState<File[] | null>(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<'andsoft' | 'csv'>('andsoft');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();

  // Flatten nested objects/arrays into dot/index path keys
  const flattenObject = (input: any, prefix = ''): Record<string, any> => {
    const out: Record<string, any> = {};
    const isObj = (val: any) => val && typeof val === 'object' && !Array.isArray(val);
    if (Array.isArray(input)) {
      input.forEach((val, idx) => {
        const key = prefix ? `${prefix}.${idx}` : String(idx);
        if (isObj(val) || Array.isArray(val)) {
          Object.assign(out, flattenObject(val, key));
        } else {
          out[key] = val;
        }
      });
    } else if (isObj(input)) {
      Object.entries(input).forEach(([k, val]) => {
        const key = prefix ? `${prefix}.${k}` : k;
        if (isObj(val) || Array.isArray(val)) {
          Object.assign(out, flattenObject(val, key));
        } else {
          out[key] = val;
        }
      });
    } else {
      out[prefix || 'value'] = input;
    }
    return out;
  };

  const convertToCSV = (data: any): string => {
    const escape = (val: any) => {
      const s = val === undefined || val === null ? '' : String(val);
      const needsQuotes = s.includes(',') || s.includes('\n') || s.includes('"');
      const escaped = s.replace(/"/g, '""');
      return needsQuotes ? `"${escaped}"` : escaped;
    };

    // If array, flatten each item and union headers
    if (Array.isArray(data)) {
      const flattenedItems = data.map(item => flattenObject(item));
      const headerSet = new Set<string>();
      flattenedItems.forEach(obj => Object.keys(obj).forEach(k => headerSet.add(k)));
      const headers = Array.from(headerSet);
      const rows = flattenedItems.map(obj => headers.map(h => escape(obj[h])));
      return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    }

    // If single object, flatten and output a single row with headers
    if (data && typeof data === 'object') {
      const flat = flattenObject(data);
      const headers = Object.keys(flat);
      const row = headers.map(h => escape(flat[h]));
      return [headers.join(','), row.join(',')].join('\n');
    }

    // Primitive fallback
    return ['value', escape(data)].join('\n');
  };

  const downloadCSV = (csv: string, filename = 'ocr_result.csv') => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;
    setDroppedFiles(fileArray);
    setError(null);

    const file = fileArray[0];

    setLoading(true);

    try {
      // Convert file to base64
      const base64String = await fileToBase64(file);
      
      // Extract just the base64 data (remove data:image/png;base64, prefix)
      const base64Data = base64String.split(',')[1] || base64String;

      const response = await apiPost(
        buildApiUrl('/api/v1/ocr/process'),
        {
          fileName: file.name,
          fileData: base64Data,
        }
      );

      if (response.ok) {
        const result = (await response.json()).result;
        if (action === 'andsoft') {
          navigate('/order', { state: { responseData: result } });
        } else {
          const csv = convertToCSV(result);
          downloadCSV(csv);
        }
      } else {
        setError(t('Failed to process document. Please try again.'));
      }
    } catch (err) {
      setError(t('Error uploading file. Please try again.'));
      console.error('Error uploading file:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      await handleFiles(files);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      await handleFiles(files);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: (theme: any) => theme.palette.background.primaryGradient,
      }}
    >
      {/* Header with back button */}
      <Box
        sx={{
          bgcolor: (theme: any) => theme.palette.background.purpleGradientDark,
          px: 2.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <IconButton
          onClick={() => navigate('/dashboard')}
          sx={{
            color: 'text.white',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography
          variant="h2"
          sx={{
            color: 'text.white',
            fontSize: (theme: any) => theme.typography.fontSizes.heading3,
            fontWeight: 600,
            ml: 1.5,
          }}
        >
          {t('OCR Document Processing')}
        </Typography>
      </Box>

      {/* Main content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          p: 2.5,
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mt: 12.5,
            }}
          >
            <CircularProgress
              size={60}
              thickness={6}
              sx={{
                mb: 2.5,
                color: 'text.purple',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                },
              }}
            />
            <Typography sx={{ color: 'text.white' }}>{t('Processing your document...')}</Typography>
          </Box>
        ) : (
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            sx={{
              width: '80%',
              height: '80%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              border: (theme: any) => `2px dashed ${theme.palette.border.purple}`,
              p: 2.5,
              bgcolor: (theme: any) => dragging ? theme.palette.background.purpleDark : theme.palette.background.purpleMedium,
              textAlign: 'center',
              transition: 'background-color 0.3s ease',
              '&:hover': {
                bgcolor: (theme: any) => theme.palette.background.purpleLight,
              },
            }}
          >
            {/* Hidden file input for picker */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
              accept=".pdf,image/*"
            />
            <Typography
              variant="h1"
              sx={{
                color: 'text.white',
                fontSize: (theme: any) => theme.typography.fontSizes.heading2,
                fontWeight: 700,
                mb: 2,
              }}
            >
              {t('Drag and Drop Files Here')}
            </Typography>
            <Button
              variant="contained"
              onClick={openFilePicker}
              sx={{
                mb: 2,
                bgcolor: (theme: any) => theme.palette.primary.main,
                color: 'text.white',
                textTransform: 'none',
                '&:hover': (theme: any) => ({ bgcolor: theme.palette.secondary.main }),
              }}
            >
              {t('Browse Files')}
            </Button>
            <Typography
              variant="body1"
              sx={{
                color: 'text.white',
                mb: 2,
              }}
            >
              {t('Drag files from your computer and they will appear below:')}
            </Typography>

            {/* Action selector */}
            <Box sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <Select
                  value={action}
                  onChange={e => setAction(e.target.value as 'andsoft' | 'csv')}
                  displayEmpty
                  sx={{
                    bgcolor: 'background.inputGrey',
                    color: 'text.white',
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  }}
                >
                  <MenuItem value={'andsoft'}>{t('Show Andsoft Screen')}</MenuItem>
                  <MenuItem value={'csv'}>{t('Download CSV')}</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {droppedFiles ? (
              <Box sx={{ mt: 2.5 }}>
                <Typography
                  variant="h3"
                  sx={{
                    color: 'text.white',
                    fontSize: (theme: any) => theme.typography.fontSizes.heading2,
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  {t('Dropped Files:')}
                </Typography>
                <List sx={{ listStyle: 'none', p: 0 }}>
                  {droppedFiles.map((file, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        color: 'text.white',
                        justifyContent: 'center',
                        py: 0.5,
                      }}
                    >
                      {file.name}
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : (
              <Typography
                variant="body1"
                sx={{
                  color: 'text.white',
                }}
              >
                {t('Drop files here!')}
              </Typography>
            )}
          </Box>
        )}
      </Box>
      {/* Error message display */}
      {error && (
        <Box
          sx={{
            bgcolor: 'background.warning',
            color: 'text.white',
            p: 2,
            textAlign: 'center',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Typography>{error}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default Drag;

